import React, { useEffect, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line,
} from "recharts";
import {
  Trophy, Star, Users, Clock, Dices, MapPin, TrendingUp,
  Trash2, CalendarDays, Filter, Crown, ThumbsDown, Zap, Timer,
  BadgeCheck, Medal, ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

// ─── palette ────────────────────────────────────────────────────────────────
const PALETTE = ["#2B593F", "#A6432E", "#1E5C7A", "#8A6B1F", "#5A4A8A", "#1E7A5C", "#7A1E5C", "#5A4636", "#2D5C7C", "#7A2020"];
const PIE_PALETTE = ["#2B593F", "#A6432E", "#1E5C7A", "#8A6B1F", "#5A4A8A", "#1E7A5C"];

// ─── helpers ─────────────────────────────────────────────────────────────────
const fmt = (mins) => {
  if (!mins) return "—";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

const MONTH_LABELS = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
const fmtMonth = (ym) => {
  if (!ym) return "";
  const [, m] = ym.split("-");
  return MONTH_LABELS[parseInt(m, 10) - 1] || ym;
};

// ─── sub-components ──────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, sub, color = "#2B593F" }) {
  return (
    <div className="bg-white dark:bg-[#111111] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-2xl p-5 flex flex-col gap-2 shadow-sm">
      <div className="flex items-center gap-2">
        <Icon size={18} style={{ color }} />
        <span className="text-xs font-semibold uppercase tracking-wider text-[#757169] dark:text-[#B8B2AA]">{label}</span>
      </div>
      <div className="font-display text-2xl font-bold text-[#2D2A26] dark:text-[#F5F1EA] leading-tight">
        {value ?? "—"}
      </div>
      {sub && <div className="text-xs text-[#757169] dark:text-[#B8B2AA]">{sub}</div>}
    </div>
  );
}

function OscarCard({ rank, icon: Icon, title, entries }) {
  if (!entries || entries.length === 0) return null;
  const icons = [Crown, Medal, BadgeCheck];
  return (
    <div className="bg-white dark:bg-[#111111] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-2xl p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <Icon size={18} className="text-[#8A6B1F]" />
        <h3 className="font-display font-bold text-[#2D2A26] dark:text-[#F5F1EA]">{title}</h3>
      </div>
      <ol className="space-y-2">
        {entries.slice(0, 5).map((e, i) => {
          const RankIcon = icons[i] || BadgeCheck;
          return (
            <li key={i} className="flex items-center gap-3 text-sm">
              <RankIcon size={14} className={i === 0 ? "text-[#8A6B1F]" : i === 1 ? "text-[#757169]" : "text-[#C8A87A]"} />
              <span className="flex-1 text-[#2D2A26] dark:text-[#F5F1EA] font-medium truncate">{e.label}</span>
              <span className="text-[#757169] dark:text-[#B8B2AA] text-xs whitespace-nowrap">{e.value}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function HistoryTable({ entries, onDelete, isSuper }) {
  const [page, setPage] = useState(0);
  const PER_PAGE = 20;
  const total = entries.length;
  const slice = entries.slice(page * PER_PAGE, (page + 1) * PER_PAGE);

  return (
    <div className="bg-white dark:bg-[#111111] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-2xl overflow-hidden shadow-sm">
      <div className="p-5 border-b border-[#EAE3D9] dark:border-[#2A2A2A]">
        <h3 className="font-display font-bold text-[#2D2A26] dark:text-[#F5F1EA] flex items-center gap-2">
          <Dices size={18} className="text-[#2B593F]" />
          Tutte le partite ({total})
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#EAE3D9] dark:border-[#2A2A2A] bg-[#F5F1EA] dark:bg-[#1A1A1A]">
              <th className="px-4 py-2.5 text-left text-[#757169] dark:text-[#B8B2AA] font-medium">Data</th>
              <th className="px-4 py-2.5 text-left text-[#757169] dark:text-[#B8B2AA] font-medium">Gioco</th>
              <th className="px-4 py-2.5 text-left text-[#757169] dark:text-[#B8B2AA] font-medium hidden sm:table-cell">Sede</th>
              <th className="px-4 py-2.5 text-left text-[#757169] dark:text-[#B8B2AA] font-medium hidden md:table-cell">Giocatori</th>
              <th className="px-4 py-2.5 text-left text-[#757169] dark:text-[#B8B2AA] font-medium hidden lg:table-cell">Durata</th>
              <th className="px-4 py-2.5 text-left text-[#757169] dark:text-[#B8B2AA] font-medium">Vincitore</th>
              {isSuper && <th className="px-4 py-2.5"></th>}
            </tr>
          </thead>
          <tbody>
            {slice.map((e, i) => (
              <tr key={e.id} className={`border-b border-[#EAE3D9]/60 dark:border-[#2A2A2A] ${i % 2 === 0 ? "" : "bg-[#FDFBF7] dark:bg-[#0E0E0E]"} hover:bg-[#F5F1EA] dark:hover:bg-[#1A1A1A] transition-colors`}>
                <td className="px-4 py-3 text-[#4A4742] dark:text-[#C8C2BA] whitespace-nowrap">{fmtDate(e.played_at)}</td>
                <td className="px-4 py-3 font-medium text-[#2D2A26] dark:text-[#F5F1EA] max-w-[160px] truncate">{e.game_name}</td>
                <td className="px-4 py-3 text-[#757169] dark:text-[#B8B2AA] hidden sm:table-cell">
                  <span className="inline-flex items-center gap-1">
                    <MapPin size={11} />
                    {e.venue}
                  </span>
                </td>
                <td className="px-4 py-3 text-[#757169] dark:text-[#B8B2AA] hidden md:table-cell">
                  {(e.players || []).map((p) => p.name).join(", ") || "—"}
                  {(e.extra_players || 0) > 0 && ` +${e.extra_players}`}
                </td>
                <td className="px-4 py-3 text-[#757169] dark:text-[#B8B2AA] hidden lg:table-cell">{fmt(e.play_time)}</td>
                <td className="px-4 py-3">
                  {e.winner ? (
                    <span className="inline-flex items-center gap-1 text-[#8A6B1F] dark:text-[#D4B87A] font-medium text-xs">
                      <Crown size={12} />
                      {e.winner.name}
                    </span>
                  ) : (
                    <span className="text-[#C8C2BA] text-xs">—</span>
                  )}
                </td>
                {isSuper && (
                  <td className="px-4 py-3">
                    <button
                      onClick={() => onDelete(e.id)}
                      className="text-[#C8C2BA] hover:text-red-400 transition-colors p-1"
                      title="Elimina"
                    >
                      <Trash2 size={13} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > PER_PAGE && (
        <div className="p-4 flex items-center justify-between text-sm text-[#757169] dark:text-[#B8B2AA] border-t border-[#EAE3D9] dark:border-[#2A2A2A]">
          <span>Pagina {page + 1} di {Math.ceil(total / PER_PAGE)}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Prec.</Button>
            <Button variant="outline" size="sm" disabled={(page + 1) * PER_PAGE >= total} onClick={() => setPage((p) => p + 1)}>Succ.</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── main page ───────────────────────────────────────────────────────────────
export default function StoricoPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  const isSuper = user?.role === "superadmin";

  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [venue, setVenue] = useState("all");
  const [years, setYears] = useState([]);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("oscar"); // oscar | history | charts

  useEffect(() => { document.title = "Tavoli da Gioco — Storico partite"; }, []);

  // load available years
  useEffect(() => {
    api.get("/admin/history/years").then(({ data }) => {
      const yrs = data.years || [];
      setYears(yrs);
      if (yrs.length > 0 && !yrs.includes(year)) setYear(yrs[0]);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (year && year !== "all") params.year = year;
      if (venue && venue !== "all") params.venue = venue;
      const [statsRes, histRes] = await Promise.all([
        api.get("/admin/history/stats", { params }),
        api.get("/admin/history", { params }),
      ]);
      setStats(statsRes.data);
      setHistory(histRes.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [year, venue]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questa voce dallo storico?")) return;
    try {
      await api.delete(`/admin/history/${id}`);
      toast.success("Voce eliminata");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (!isAdmin) return <Navigate to="/" replace />;

  // Oscar cards data
  const oscarCards = stats ? [
    {
      icon: Dices,
      title: "Gioco più giocato",
      entries: stats.most_played_games.map((g) => ({ label: g.game_name, value: `${g.count}×` })),
    },
    {
      icon: Crown,
      title: "Giocatore più vincente",
      entries: stats.player_wins.map((p) => ({ label: p.player_name, value: `${p.count} vinte` })),
    },
    {
      icon: Users,
      title: "Più presenze",
      entries: stats.player_appearances.map((p) => ({ label: p.player_name, value: `${p.count} serate` })),
    },
    {
      icon: ThumbsDown,
      title: "Giocatore più sconfitto",
      entries: stats.player_losses.map((p) => ({ label: p.player_name, value: `${p.count} perse` })),
    },
    {
      icon: Star,
      title: "Più punti totali",
      entries: stats.player_total_points.map((p) => ({ label: p.player_name, value: `${p.total.toFixed(0)} pt` })),
    },
    {
      icon: Zap,
      title: "Giocato solo una volta",
      entries: stats.games_played_once.map((g) => ({ label: g.game_name, value: "1×" })),
    },
  ] : [];

  const monthData = (stats?.games_per_month || []).map((m) => ({
    name: fmtMonth(m.month),
    partite: m.count,
    month: m.month,
  }));

  const categoryData = (stats?.games_per_category || []).map((c) => ({
    name: c.category,
    value: c.count,
  }));

  return (
    <div className="min-h-screen bg-[#FDFBF7] dark:bg-[#0B0B0B]">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#757169] dark:text-[#B8B2AA] hover:text-[#2B593F] dark:hover:text-[#8FC79F] transition-colors">
            <ArrowLeft size={14} />
            Torna alla dashboard
          </Link>
        </div>

        {/* Page title */}
        <div className="flex flex-col sm:flex-row sm:items-end gap-4 mb-8">
          <div className="flex-1">
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#2D2A26] dark:text-[#F5F1EA] flex items-center gap-3">
              <Trophy size={32} className="text-[#8A6B1F]" />
              Storico Partite
            </h1>
            <p className="text-[#757169] dark:text-[#B8B2AA] mt-1">Oscar del gioco da tavolo — statistiche annuali</p>
          </div>
          {/* Filters */}
          <div className="flex flex-wrap gap-2 items-center">
            <Filter size={14} className="text-[#757169]" />
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-28 h-8 text-sm bg-white dark:bg-[#111111] border-[#EAE3D9] dark:border-[#2A2A2A]">
                <SelectValue placeholder="Anno" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                {!years.includes(String(new Date().getFullYear())) && (
                  <SelectItem value={String(new Date().getFullYear())}>{new Date().getFullYear()}</SelectItem>
                )}
              </SelectContent>
            </Select>
            <Select value={venue} onValueChange={setVenue}>
              <SelectTrigger className="w-36 h-8 text-sm bg-white dark:bg-[#111111] border-[#EAE3D9] dark:border-[#2A2A2A]">
                <SelectValue placeholder="Sede" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le sedi</SelectItem>
                <SelectItem value="Bibliocronache">Bibliocronache</SelectItem>
                <SelectItem value="Patrogames">Patrogames</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20 text-[#757169]">
            Caricamento storico...
          </div>
        )}

        {!loading && stats && (
          <>
            {/* Top KPI cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
              <StatCard icon={Dices} label="Partite totali" value={stats.total_games} color="#2B593F" />
              <StatCard icon={Clock} label="Ore di gioco" value={stats.total_play_time > 0 ? fmt(stats.total_play_time) : "—"} color="#1E5C7A" />
              <StatCard icon={Timer} label="Partita più lunga" value={stats.longest_game?.game_name || "—"} sub={stats.longest_game ? fmt(stats.longest_game.play_time) : ""} color="#A6432E" />
              <StatCard icon={Zap} label="Partita più breve" value={stats.shortest_game?.game_name || "—"} sub={stats.shortest_game ? fmt(stats.shortest_game.play_time) : ""} color="#8A6B1F" />
              <StatCard icon={Crown} label="Campione" value={stats.player_wins[0]?.player_name || "—"} sub={stats.player_wins[0] ? `${stats.player_wins[0].count} vittorie` : ""} color="#8A6B1F" />
              <StatCard icon={Users} label="Presenze (1°)" value={stats.player_appearances[0]?.player_name || "—"} sub={stats.player_appearances[0] ? `${stats.player_appearances[0].count} partite` : ""} color="#5A4A8A" />
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mb-6 bg-[#F5F1EA] dark:bg-[#1A1A1A] rounded-xl p-1 w-fit">
              {[
                { key: "oscar", label: "Oscar", icon: Trophy },
                { key: "charts", label: "Grafici", icon: TrendingUp },
                { key: "history", label: "Lista partite", icon: CalendarDays },
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === key
                      ? "bg-white dark:bg-[#111111] text-[#2D2A26] dark:text-[#F5F1EA] shadow-sm"
                      : "text-[#757169] dark:text-[#B8B2AA] hover:text-[#2D2A26] dark:hover:text-[#F5F1EA]"
                  }`}
                >
                  <Icon size={14} />
                  {label}
                </button>
              ))}
            </div>

            {/* OSCAR TAB */}
            {activeTab === "oscar" && (
              <div className="space-y-6">
                {stats.total_games === 0 ? (
                  <div className="text-center py-16 text-[#757169] dark:text-[#B8B2AA]">
                    <Trophy size={40} className="mx-auto mb-3 opacity-30" />
                    <p>Nessuna partita registrata per questo periodo.</p>
                    <p className="text-sm mt-1">Usa il tasto <strong>Chiudi partita</strong> sui tavoli per iniziare a costruire lo storico.</p>
                  </div>
                ) : (
                  <>
                    {/* Longest / shortest */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {stats.longest_game && (
                        <div className="bg-white dark:bg-[#111111] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-2xl p-5 shadow-sm">
                          <div className="flex items-center gap-2 mb-2">
                            <Timer size={16} className="text-[#A6432E]" />
                            <span className="text-xs font-semibold uppercase tracking-wider text-[#757169] dark:text-[#B8B2AA]">Partita più lunga</span>
                          </div>
                          <p className="font-display text-xl font-bold text-[#2D2A26] dark:text-[#F5F1EA]">{stats.longest_game.game_name}</p>
                          <p className="text-sm text-[#757169] dark:text-[#B8B2AA] mt-1">{fmt(stats.longest_game.play_time)} · {fmtDate(stats.longest_game.played_at)} · {stats.longest_game.venue}</p>
                        </div>
                      )}
                      {stats.shortest_game && (
                        <div className="bg-white dark:bg-[#111111] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-2xl p-5 shadow-sm">
                          <div className="flex items-center gap-2 mb-2">
                            <Zap size={16} className="text-[#8A6B1F]" />
                            <span className="text-xs font-semibold uppercase tracking-wider text-[#757169] dark:text-[#B8B2AA]">Partita più breve</span>
                          </div>
                          <p className="font-display text-xl font-bold text-[#2D2A26] dark:text-[#F5F1EA]">{stats.shortest_game.game_name}</p>
                          <p className="text-sm text-[#757169] dark:text-[#B8B2AA] mt-1">{fmt(stats.shortest_game.play_time)} · {fmtDate(stats.shortest_game.played_at)} · {stats.shortest_game.venue}</p>
                        </div>
                      )}
                    </div>

                    {/* Oscar grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {oscarCards.map((card, i) => (
                        <OscarCard key={i} {...card} />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* CHARTS TAB */}
            {activeTab === "charts" && (
              <div className="space-y-6">
                {stats.total_games === 0 ? (
                  <div className="text-center py-16 text-[#757169] dark:text-[#B8B2AA]">
                    <TrendingUp size={40} className="mx-auto mb-3 opacity-30" />
                    <p>Nessun dato disponibile per i grafici.</p>
                  </div>
                ) : (
                  <>
                    {/* Partite per mese */}
                    {monthData.length > 0 && (
                      <div className="bg-white dark:bg-[#111111] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-2xl p-5 shadow-sm">
                        <h3 className="font-display font-bold text-[#2D2A26] dark:text-[#F5F1EA] mb-4 flex items-center gap-2">
                          <CalendarDays size={16} className="text-[#2B593F]" />
                          Partite per mese
                        </h3>
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart data={monthData} margin={{ top: 0, right: 10, left: -10, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#EAE3D9" />
                            <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#757169" }} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#757169" }} />
                            <ReTooltip
                              contentStyle={{ background: "#FDFBF7", border: "1px solid #EAE3D9", borderRadius: 8, fontSize: 13 }}
                              formatter={(v) => [v, "Partite"]}
                            />
                            <Bar dataKey="partite" fill="#2B593F" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Giochi più giocati */}
                      {stats.most_played_games.length > 0 && (
                        <div className="bg-white dark:bg-[#111111] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-2xl p-5 shadow-sm">
                          <h3 className="font-display font-bold text-[#2D2A26] dark:text-[#F5F1EA] mb-4 flex items-center gap-2">
                            <Dices size={16} className="text-[#2B593F]" />
                            Top giochi più giocati
                          </h3>
                          <ResponsiveContainer width="100%" height={220}>
                            <BarChart
                              layout="vertical"
                              data={stats.most_played_games.slice(0, 8).map((g) => ({ name: g.game_name.length > 20 ? g.game_name.slice(0, 18) + "…" : g.game_name, value: g.count }))}
                              margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#EAE3D9" />
                              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: "#757169" }} />
                              <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 11, fill: "#757169" }} />
                              <ReTooltip contentStyle={{ background: "#FDFBF7", border: "1px solid #EAE3D9", borderRadius: 8, fontSize: 13 }} formatter={(v) => [v, "Partite"]} />
                              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                                {stats.most_played_games.slice(0, 8).map((_, i) => (
                                  <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                                ))}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}

                      {/* Categorie */}
                      {categoryData.length > 0 && (
                        <div className="bg-white dark:bg-[#111111] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-2xl p-5 shadow-sm">
                          <h3 className="font-display font-bold text-[#2D2A26] dark:text-[#F5F1EA] mb-4 flex items-center gap-2">
                            <Filter size={16} className="text-[#2B593F]" />
                            Categorie giocate
                          </h3>
                          <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                              <Pie
                                data={categoryData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={80}
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                labelLine={false}
                              >
                                {categoryData.map((_, i) => (
                                  <Cell key={i} fill={PIE_PALETTE[i % PIE_PALETTE.length]} />
                                ))}
                              </Pie>
                              <ReTooltip contentStyle={{ background: "#FDFBF7", border: "1px solid #EAE3D9", borderRadius: 8, fontSize: 13 }} />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      )}

                      {/* Top presenze */}
                      {stats.player_appearances.length > 0 && (
                        <div className="bg-white dark:bg-[#111111] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-2xl p-5 shadow-sm">
                          <h3 className="font-display font-bold text-[#2D2A26] dark:text-[#F5F1EA] mb-4 flex items-center gap-2">
                            <Users size={16} className="text-[#2B593F]" />
                            Giocatori più presenti
                          </h3>
                          <ResponsiveContainer width="100%" height={220}>
                            <BarChart
                              layout="vertical"
                              data={stats.player_appearances.slice(0, 8).map((p) => ({ name: p.player_name, value: p.count }))}
                              margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#EAE3D9" />
                              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: "#757169" }} />
                              <YAxis dataKey="name" type="category" width={90} tick={{ fontSize: 11, fill: "#757169" }} />
                              <ReTooltip contentStyle={{ background: "#FDFBF7", border: "1px solid #EAE3D9", borderRadius: 8, fontSize: 13 }} formatter={(v) => [v, "Partite"]} />
                              <Bar dataKey="value" fill="#5A4A8A" radius={[0, 4, 4, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}

                      {/* Top vincitori */}
                      {stats.player_wins.length > 0 && (
                        <div className="bg-white dark:bg-[#111111] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-2xl p-5 shadow-sm">
                          <h3 className="font-display font-bold text-[#2D2A26] dark:text-[#F5F1EA] mb-4 flex items-center gap-2">
                            <Crown size={16} className="text-[#8A6B1F]" />
                            Classifica vittorie
                          </h3>
                          <ResponsiveContainer width="100%" height={220}>
                            <BarChart
                              layout="vertical"
                              data={stats.player_wins.slice(0, 8).map((p) => ({ name: p.player_name, value: p.count }))}
                              margin={{ top: 0, right: 20, left: 0, bottom: 0 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#EAE3D9" />
                              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: "#757169" }} />
                              <YAxis dataKey="name" type="category" width={90} tick={{ fontSize: 11, fill: "#757169" }} />
                              <ReTooltip contentStyle={{ background: "#FDFBF7", border: "1px solid #EAE3D9", borderRadius: 8, fontSize: 13 }} formatter={(v) => [v, "Vittorie"]} />
                              <Bar dataKey="value" fill="#8A6B1F" radius={[0, 4, 4, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* HISTORY TAB */}
            {activeTab === "history" && (
              <HistoryTable entries={history} onDelete={handleDelete} isSuper={isSuper} />
            )}
          </>
        )}
      </main>
    </div>
  );
}
